import { useCallback, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { apiFetch } from "@/lib/api/client";
import { API_HOST, BACKEND_PREFIX } from "@/lib/api/config";
import { useAuth } from "@/lib/auth/AuthProvider";

/**
 * Theme (V5-36) — three modes, one stored per user.
 *
 * `light` and `dark` are the two palettes the `cp-*` tokens already define in
 * `styles/globals.css` (`:root` and `.dark`). `system` is the DEFAULT and is
 * not a third palette: it resolves to one of the other two by following
 * `prefers-color-scheme`, and keeps following it if the OS flips at sunset.
 *
 * SYSTEM IS THE DEFAULT, WHICH IS A CHANGE
 * -----------------------------------------
 * The SPA previously defaulted to DARK when nothing was stored (FE-05), and
 * the no-flash script in `index.html` did the same. Both now default to
 * `system`. The server has agreed all along — `DEFAULT_THEME_MODE = "system"`
 * in `backend/api/app/schemas/preference.py` — so this is the client catching up
 * to the stored contract, not a new opinion. A user who had explicitly chosen
 * dark keeps it: their choice is in `localStorage` and in the database.
 *
 * WHERE THE VALUE LIVES
 * ---------------------
 * `localStorage["aim-theme"]` is the FIRST-PAINT copy, read by the inline
 * script in `index.html` before React exists — that is what stops a dark-mode
 * user seeing a white flash. `rent.user_preference` is the durable per-user
 * copy, fetched by `ThemeProvider` after sign-in and written on every change.
 * localStorage is a cache of the server value.
 *
 * ACCENT IS CARRIED, NOT APPLIED
 * ------------------------------
 * The stored theme preference also has an `accent` hex. It is read and written
 * back untouched so an existing value is never destroyed, but it is
 * deliberately NOT pushed onto `--cp-accent`: that token is one of the ones
 * that DIFFERS between the two palettes (`#1e40af` light, `#4c9aff` dark), so
 * pinning it to a single hex would flatten the light/dark distinction the
 * palette is built on. Per-user accent needs a token pair, which is a design
 * change, not a wiring one. V5-36 asked for dark/light.
 */

export const THEME_STORAGE_KEY = "aim-theme";

/** What is stored and chosen. */
export type ThemeMode = "light" | "dark" | "system";
/** What is actually painted. `system` has already been resolved. */
export type ThemeName = "light" | "dark";

export const DEFAULT_MODE: ThemeMode = "system";
/** Mirrors `DEFAULT_ACCENT` in `backend/api/app/schemas/preference.py`. */
export const DEFAULT_ACCENT = "#2563eb";

const PREFS_URL = `${API_HOST}${BACKEND_PREFIX.fastapi}/preferences`;

function isMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * What `system` resolves to when the OS has not been asked to decide — set by
 * the Wallet DESKTOP frame, whose approved design (the MDM console) is
 * light-first. `null` means
 * "follow prefers-color-scheme" (the default). An explicit `light`/`dark`
 * choice by the user is never overridden by this.
 */
let systemFallback: ThemeName | null = null;

/**
 * The same rule the no-flash script in `index.html` applies, evaluated here so
 * the two agree on the FIRST paint. Without this the module-load `apply()`
 * below would disagree with the script and the desktop would flash between
 * themes as `DesktopFrame` mounted.
 *
 * Public pages are excluded because they are not the app: `/login` is designed
 * light and is what a signed-out visitor sees.
 */
const PUBLIC_PREFIXES = ["/login", "/access-request-pending", "/no-access", "/share"];

export function desktopLightDefault(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  if (!window.matchMedia("(min-width: 1024px)").matches) return false;
  const path = window.location?.pathname ?? "";
  return !PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

export function setSystemFallback(next: ThemeName | null): void {
  if (systemFallback === next) return;
  systemFallback = next;
  if (mode === "system") {
    apply("system");
    emit();
  }
}

/** `system` -> the shell's fallback if one is set, else whatever the OS says. */
export function resolveMode(mode: ThemeMode): ThemeName {
  if (mode === "system") return systemFallback ?? (prefersDark() ? "dark" : "light");
  return mode;
}

function readStoredMode(): ThemeMode {
  if (typeof localStorage === "undefined") return DEFAULT_MODE;
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    // Values written before V5 were the bare strings "light"/"dark", which are
    // still valid modes — so old storage upgrades itself with no migration.
    return isMode(raw) ? raw : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

let mode: ThemeMode = readStoredMode();
let accent: string = DEFAULT_ACCENT;
let revision = 0;

const listeners = new Set<() => void>();

function emit(): void {
  revision += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Paint. The single place `.dark` is added or removed. */
function apply(next: ThemeMode): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveMode(next) === "dark");
}

export function getMode(): ThemeMode {
  return mode;
}

export function getAccent(): string {
  return accent;
}

/**
 * Set the mode locally and repaint. Does NOT talk to the server —
 * `ThemeProvider` owns that, so this stays usable before sign-in and in tests.
 */
export function setMode(next: ThemeMode): void {
  if (!isMode(next)) return;
  mode = next;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Private browsing: the theme still applies this session, it just will not
    // survive a reload.
  }
  apply(next);
  emit();
}

/** Carried through the round-trip; see the module note on why it is not applied. */
export function setAccent(next: string): void {
  accent = next;
  emit();
}

/**
 * Follow the OS while (and only while) the mode is `system`.
 *
 * Registered once at module load rather than in a hook: the listener has to
 * outlive any particular component, and a `system` user whose laptop flips to
 * dark at sunset should see the app flip with it without having to have a
 * specific screen mounted.
 */
if (typeof window !== "undefined" && window.matchMedia) {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (mode === "system") {
      apply("system");
      emit();
    }
  };
  if (query.addEventListener) query.addEventListener("change", onChange);
  else query.addListener(onChange); // Safari < 14
}

// Seed the desktop light-first default before the first `apply()` so this
// module and the no-flash script agree; `DesktopFrame` keeps it in sync with
// the viewport from there.
if (desktopLightDefault()) systemFallback = "light";

// Reconcile the class the no-flash script set with what is actually stored.
// They agree in every normal case; this covers the one where storage was
// written by another tab after that script ran.
apply(mode);

interface ThemeApi {
  /** The palette actually painted right now. */
  theme: ThemeName;
  /** What the user chose, which may be `system`. */
  mode: ThemeMode;
  accent: string;
  setMode(next: ThemeMode): void;
  /** Back-compat: pick a palette explicitly. Equivalent to `setMode`. */
  setTheme(next: ThemeName): void;
  /** Flip to the opposite of what is on screen, pinning the choice. */
  toggle(): void;
}

/**
 * Canonical theme hook.
 *
 * `ThemeToggle` and every other caller goes through here. The returned
 * `theme`/`setTheme`/`toggle` triple is the pre-V5 API, unchanged, so no
 * existing call site had to move; `mode`/`setMode` are the addition that makes
 * `system` expressible.
 *
 * `useSyncExternalStore` rather than `useState` + effect: the store is
 * module-level (the media-query listener writes to it from outside React), and
 * the old implementation's hydration dance — seed `"light"`, correct in an
 * effect — existed only to satisfy a server renderer the SPA no longer has.
 */
export function useTheme(): ThemeApi {
  const currentMode = useSyncExternalStore(
    subscribe,
    getMode,
    () => DEFAULT_MODE,
  );
  const currentAccent = useSyncExternalStore(subscribe, getAccent, () => DEFAULT_ACCENT);
  const resolved = resolveMode(currentMode);

  const setThemeExplicit = useCallback((next: ThemeName) => setMode(next), []);
  const toggle = useCallback(() => {
    setMode(resolveMode(getMode()) === "dark" ? "light" : "dark");
  }, []);

  return {
    theme: resolved,
    mode: currentMode,
    accent: currentAccent,
    setMode,
    setTheme: setThemeExplicit,
    toggle,
  };
}

/**
 * Subscribes to theme changes — for components (e.g. Recharts, which takes
 * literal colour strings rather than CSS vars) that need a different palette
 * per theme at render time.
 *
 * Reads the store rather than observing the `<html>` class with a
 * `MutationObserver`, which is what this used to do. Same answer, no DOM
 * observer, and it now also updates when a `system` user's OS flips.
 */
export function useIsDarkMode(): boolean {
  const currentMode = useSyncExternalStore(subscribe, getMode, () => DEFAULT_MODE);
  return resolveMode(currentMode) === "dark";
}

/**
 * Reconciles the module store with the per-user server copy.
 *
 * Returns `children` untouched — it renders no chrome, it just runs effects,
 * which is why this lives in a `.ts` file with no JSX.
 *
 * Same ordering rule as `PrivacyProvider`: localStorage wins the first paint
 * (the inline script in `index.html`), the server wins once the fetch resolves.
 * A failed fetch leaves the local choice in place rather than snapping someone
 * back to a default mid-session.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const { status, user } = useAuth();

  useEffect(() => {
    if (status !== "authenticated" || !user) return;
    let cancelled = false;
    apiFetch(PREFS_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled || !body?.theme) return;
        if (isMode(body.theme.mode)) setMode(body.theme.mode);
        if (typeof body.theme.accent === "string") setAccent(body.theme.accent);
      })
      .catch(() => {
        /* keep the local choice */
      });
    return () => {
      cancelled = true;
    };
    // Re-fetch when the signed-in user changes, not on every render.
  }, [status, user?.id]);

  return children;
}

/**
 * Persist a theme choice for the signed-in user.
 *
 * Optimistic: the repaint happens immediately and the write follows. A failed
 * write leaves the theme applied for this session and simply not durable,
 * which beats a toggle that visibly bounces back.
 */
export async function saveTheme(next: ThemeMode): Promise<void> {
  setMode(next);
  try {
    await apiFetch(`${PREFS_URL}/theme`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode: next, accent: getAccent() }),
    });
  } catch {
    /* see above */
  }
}
